import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTenant } from '../../../contexts/TenantContext';
import { useStaff } from '../StaffContext';
import useAdminButton from '../hooks/useAdminButton';
import PersonAvatar from './PersonAvatar';

/**
 * Whose calendar the admin works on: one button per provider, with a blank avatar and short name
 * (docs/MULTI_PROVIDER.md, flow 4). Only for an admin of a tenant with several providers; everyone
 * else has one calendar and sees nothing here. The shown one is filled as the dashboard's buttons are.
 * The dashboard's own: the guest's choice page is a different component on purpose.
 */
const ProviderSelect = ({ sx }) => {
  const { providers, displayConfig } = useTenant();
  const { canChooseProvider, shownProviderId, showProvider } = useStaff();
  const { filledSx } = useAdminButton();
  if (!canChooseProvider) return null;

  return (
    <Stack spacing={1} alignItems="center" sx={sx} data-testid="provider-select">
      <Typography variant="body2" fontWeight={600} color="text.secondary">
        {displayConfig?.providerLabel}
      </Typography>
      <ToggleButtonGroup
        exclusive
        value={shownProviderId}
        // A click on the shown one would clear the choice; there is always one calendar on screen.
        onChange={(_event, providerId) => providerId !== null && showProvider(providerId)}
        sx={{ flexWrap: 'wrap', justifyContent: 'center', bgcolor: '#fff' }}
      >
        {providers.map((provider) => (
          <ToggleButton
            key={provider.id}
            value={provider.id}
            data-testid="provider-select-option"
            data-provider-id={provider.id}
            sx={{
              textTransform: 'none',
              gap: 1,
              px: 2,
              '&.Mui-selected': filledSx,
            }}
          >
            <PersonAvatar size={26} />
            <Box component="span" fontWeight={600}>{provider.nickname || provider.name}</Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
};

export default ProviderSelect;
